import { Button, Chips, Css, useTestIds } from "@homebound/beam";
import { BidContractRevisionFragment } from "src/routes/developments/procurement/DevelopmentContractPage.generated";
import { ProjectBidContractOverviewPage_RevisionFragment } from "src/routes/projects/bid-contracts/ProjectBidContractOverviewPage.generated";
import { createBidPackagesDetailUrl } from "src/RouteUrls";
import { centsToDollars, formatNumberToString } from "src/utils";

type ContractOverviewProps = {
  selectedRevision: BidContractRevisionFragment | ProjectBidContractOverviewPage_RevisionFragment;
};

type TemplateTotalCost = Record<string, number>;

export function BidContractOverview({ selectedRevision }: ContractOverviewProps) {
  const { bidRequest, internalEstimateBidPackage } = selectedRevision.bidContract;
  const bidPackage = bidRequest?.bidPackage ?? internalEstimateBidPackage;
  const costCodes = selectedRevision.costCodes.map(({ number }) => number) || [];
  const templateTotalCosts = buildTemplateTotals(selectedRevision);

  const tid = useTestIds({});

  return (
    <>
      <div {...tid.contractScopeCard} css={Css.wPx(440).ba.bcGray300.br16.pxPx(17).ptPx(16).pbPx(30).mr2.$}>
        <div css={Css.df.jcsb.$}>
          <div css={Css.md.mbPx(11).$}>Contract Scope</div>
          {bidPackage && (
            <Button
              variant="tertiary"
              label="Bid Package"
              onClick={createBidPackagesDetailUrl(bidPackage.bidPackageGroup!.id, bidPackage.id)}
            />
          )}
        </div>
        <div css={Css.xs2Sb.$}>Cost Codes</div>
        <div css={Css.mbPx(26).mtPx(10).$}>
          <Chips values={costCodes} />
        </div>
        <div {...tid.templateListHeader} css={Css.mbPx(16).df.fdr.jcsb.w("100%").xs2Sb.gray700.$}>
          <div>Templates ({Object.keys(templateTotalCosts).length})</div>
          <div>Total Committed</div>
        </div>
        <div {...tid.templateList} css={Css.df.fdc.$}>
          {Object.entries(templateTotalCosts).map(([templateName, totalCost], i, templates) => (
            <div
              key={templateName}
              data-testid="templateRow"
              css={
                Css.df.fdr.jcsb
                  .pyPx(8)
                  .mhPx(32)
                  .if(i + 1 !== templates.length).bb.bw1.bcGray300.$
              }
            >
              <div {...tid.templateName} css={Css.xs.$}>
                {templateName}
              </div>
              <div {...tid.totalCostInCents} css={Css.xs.$}>{` $${formatNumberToString(
                centsToDollars(totalCost),
              )}`}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * Builds the template totals for the selected revision.
 * For plan-based line items (v2), we group by ProductOffering via groupLine.group.productOffering.
 * For item/template-based line items (v1), we group by ItemTemplateItem.parent.displayName or BidItem.name.
 */
function buildTemplateTotals(
  selectedRevision: BidContractRevisionFragment | ProjectBidContractOverviewPage_RevisionFragment,
): TemplateTotalCost {
  if (selectedRevision.planBasedLineItems.nonEmpty) {
    return selectedRevision.planBasedLineItems
      .groupByObject((pbli) => pbli.groupLine.group.productOffering)
      .mapToObject(([pof, items]) => [pof.name, items.sum((pbli) => pbli.totalCostInCents)]);
  } else {
    return selectedRevision.lineItems
      .groupBy((bcli) => (bcli.ofIti ? bcli.itemTemplateItem!.parent.displayName : bcli.bidItem!.name))
      .toEntries()
      .mapToObject(([displayName, costs]) => [displayName, costs.sum((cost) => cost.totalCostInCents)]);
  }
}
